import React, { useEffect, useRef, useState } from "react";
import { api, errorMsg } from "../api";
import QuoteModal from "../../Clients/quotes";

export default function StepQuote({ projectId, onFixBom, initialQuote, initialPcbQty }) {
  const [leadDays, setLeadDays] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [quote, setQuote] = useState(initialQuote || null);
  const [pcbQty, setPcbQty] = useState(initialPcbQty || 1);
  const [blockingList, setBlockingList] = useState([]);
  const [lastGeneratedQty, setLastGeneratedQty] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const countdownRef = useRef(null);
  const redirectRef = useRef(null);

  const generatePath = `/user/projects/${projectId}/generate-quote/`;
  useEffect(() => {
    if (initialPcbQty && initialPcbQty > 0) setPcbQty(initialPcbQty);
  }, [initialPcbQty]);
  useEffect(() => {
    if (initialQuote) {
      setQuote(initialQuote);
      const metaQty = Number(initialQuote?.pricing_meta?.pcb_qty || 0);
      if (metaQty > 0) setLastGeneratedQty(metaQty);

      if (!initialQuote.is_partial) {
        setModalOpen(true);
      } else {
        setBlockingList(initialQuote.blocking || []);
      }
    }
  }, [initialQuote]);
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, []);
  const isStale = lastGeneratedQty !== null && Number(pcbQty) !== lastGeneratedQty;
  const bomRedirectReasons = new Set(["quantity_changed", "bom_changed", "bom_blocking"]);

  const startAutoRedirect = () => {
    setRedirectCountdown(3);
    let count = 3;
    countdownRef.current = setInterval(() => {
      count -= 1;
      setRedirectCountdown(count);
      if (count <= 0) {
        clearInterval(countdownRef.current);
        setRedirectCountdown(null);
        if (projectId) {
          localStorage.setItem(`pcb_auto_price_${projectId}`, "1");
        }
        onFixBom?.();
      }
    }, 1000);
  };

  const cancelAutoRedirect = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setRedirectCountdown(null);
  };

  const generateQuote = async () => {
    setError("");
    setBlockingList([]);
    cancelAutoRedirect();
    setLoading(true);

    const qty = Math.max(1, parseInt(pcbQty, 10) || 1);

    try {
      await api.patch(`/user/projects/${projectId}/quantity/`, { quantity: qty });
    } catch (_) {}

    try {
      const res = await api.post(
        generatePath,
        { lead_days: leadDays, pcb_qty: qty },
        { headers: { "Content-Type": "application/json" } }
      );

      const q = res.data?.quote || res.data;
      setLastGeneratedQty(qty);

      if (q?.is_partial) {
        setQuote(q);
        setBlockingList(q.blocking || []);
        setModalOpen(false);
        startAutoRedirect();
      } else {
        setQuote(q);
        setBlockingList([]);
        setModalOpen(true);
      }
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data || {};

      if (status === 409) {
        const blocking = data.blocking || [];
        const staleReasons = data?.workflow_state?.stale_reasons || data?.stale_reasons || [];
        const detail = data.detail || "Devis impossible : verifiez la BOM et la config PCB.";
        const shouldRedirectToBom =
          blocking.length > 0 || staleReasons.some((reason) => bomRedirectReasons.has(String(reason || "").trim()));

        setBlockingList(blocking);
        setModalOpen(false);

        if (blocking.length > 0) {
          setQuote({ is_partial: true, blocking, blocking_count: blocking.length });
        } else {
          setQuote(null);
        }

        if (shouldRedirectToBom) {
          setError(blocking.length > 0 ? "" : detail);
          startAutoRedirect();
        } else {
          setError(detail);
        }
      } else {
        setError(errorMsg(e, "Erreur génération devis"));
      }
    } finally {
      setLoading(false);
    }
  };

  const goFixBomNow = () => {
    cancelAutoRedirect();
    if (projectId) {
      localStorage.setItem(`pcb_auto_price_${projectId}`, "1");
    }
    onFixBom?.();
  };

  const nBlocking = blockingList.length;

  return (
    <div className="stepper-section">
      <h2>5) Devis</h2>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, marginBottom: 16 }}>
        <div className="form-row" style={{ minWidth: 140 }}>
          <label className="label">Quantité de cartes</label>
          <input
            type="number"
            className="input"
            min="1"
            step="1"
            value={pcbQty}
            onChange={(e) => setPcbQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
            style={{ width: 100 }}
          />
        </div>
        <div className="form-row" style={{ minWidth: 140 }}>
          <label className="label">Délai (jours)</label>
          <select value={leadDays} onChange={(e) => setLeadDays(Number(e.target.value))}>
            {[1, 2, 3, 5, 7, 10, 15, 20].map((d) => (
              <option key={d} value={d}>{d} jour{d > 1 ? "s" : ""}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Avertissement quantite obsolete */}
      {isStale && !loading && (
        <div className="alert error" style={{ marginBottom: 14 }}>
          <b>Quantité modifiée</b> : le devis a été calculé pour {lastGeneratedQty} carte{lastGeneratedQty > 1 ? "s" : ""}, vous avez maintenant {pcbQty} carte{Number(pcbQty) > 1 ? "s" : ""}.
          {" "}<b>Recalculez le devis pour continuer.</b>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button className="btn primary" onClick={generateQuote} disabled={loading}>
          {loading ? "Calcul en cours..." : isStale ? "Recalculer le devis" : "Générer / Recalculer le devis"}
        </button>

        {quote && !quote.is_partial && !isStale && (
          <button className="btn" onClick={() => setModalOpen(true)}>
            Voir le devis
          </button>
        )}
      </div>

      {/* Blocage : composants manquants / stock insuffisant */}
      {quote?.is_partial && (
        <div className="alert error" style={{ marginTop: 14 }}>
          <b>Devis impossible : {nBlocking} composant{nBlocking > 1 ? "s" : ""} bloquant{nBlocking > 1 ? "s" : ""}</b>
          <ul style={{ marginTop: 6, paddingLeft: 18 }}>
            {blockingList.slice(0, 10).map((b, idx) => (
              <li key={idx}><b>{b.mpn}</b> : {b.reason}</li>
            ))}
            {nBlocking > 10 && <li>... et {nBlocking - 10} autres</li>}
          </ul>
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {redirectCountdown !== null ? (
              <span style={{ opacity: 0.85 }}>
                Redirection vers la BOM dans <b>{redirectCountdown}s</b>...
              </span>
            ) : null}
            <button className="btn primary" onClick={goFixBomNow}>
              Corriger la BOM maintenant
            </button>
            {redirectCountdown !== null && (
              <button className="btn" onClick={cancelAutoRedirect}>
                Rester ici
              </button>
            )}
          </div>
        </div>
      )}

      {redirectCountdown !== null && nBlocking === 0 && (
        <div className="alert error" style={{ marginTop: 14 }}>
          <b>Retour a la BOM requis</b>
          <div style={{ marginTop: 8 }}>
            Redirection vers la BOM dans <b>{redirectCountdown}s</b>...
          </div>
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button className="btn primary" onClick={goFixBomNow}>
              Revenir a la BOM maintenant
            </button>
            <button className="btn" onClick={cancelAutoRedirect}>
              Rester ici
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="alert error" style={{ marginTop: 12 }}>{error}</div>
      )}

      <QuoteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        quote={quote}
        projectId={projectId}
        onFixBom={() => {
          setModalOpen(false);
          goFixBomNow();
        }}
      />
    </div>
  );
}
